/**
 * Bundled DSA Registration Pass — Terms and Conditions (includes refund policy §4).
 * Source: product TXT; only Integrity Policy / Terms of Service need HTML links.
 */
export const DSA_INTEGRITY_POLICY_URL = 'https://www.dsaexam.com/integrity.html'

export const DSA_INTEGRITY_POLICY_LINK = `<a href="${DSA_INTEGRITY_POLICY_URL}" target="_blank" rel="noopener noreferrer">DSA Integrity Policy</a>`

export const DSA_PASS_TERMS_HTML = `
<p><strong>DSA Registration Pass — Terms and Conditions</strong></p>
<p>By purchasing a Deep Scholastic Assessment (DSA) Registration Pass, you agree to the terms below and to the DSA <a href="https://www.dsaexam.com/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>.</p>

<p><strong>1. Product</strong></p>
<p>The DSA Registration Pass includes six (6) exam attempts valid for three (3) years from the purchase date. Attempts may be used on any DSA Concentration Subjects offered in published quarterly windows.</p>

<p><strong>2. Registration and use</strong></p>
<ul>
  <li>Each Concentration Subject is offered once per year in its assigned quarterly window.</li>
  <li>Registration opens only for the current quarterly window and closes on the published deadline for that cycle.</li>
  <li>You may use attempts across different subjects and windows within the validity period.</li>
  <li>A maximum of three (3) attempts is allowed per subject.</li>
  <li>An attempt is consumed when an examination session begins, or when you fail to appear for a booked session (a "no-show"). A no-show consumes one attempt.</li>
</ul>

<p><strong>3. Fees</strong></p>
<ul>
  <li>All prices are listed in USD unless otherwise stated.</li>
  <li>Automatic region- and school-based fee reductions may apply; the final payable amount is shown before checkout.</li>
  <li>Payment-processing charges, where applicable, are shown separately and are not part of the registration fee.</li>
</ul>

<p><strong>4. Refund policy</strong></p>
<p>To request a refund, go to your Profile — Pass Purchase History. If you are eligible, a Refund button will be displayed, and the refund will be processed automatically in accordance with this policy.</p>
<ul>
  <li>If you have never booked a subject, the registration fee you paid is refunded in full.</li>
  <li>If you have booked any subject — including a booking later cancelled or changed — but have not sat an examination, fifty percent (50%) of the registration fee you paid is refunded. A booking, once made, counts as use of the Pass.</li>
  <li>If any attempt has been consumed, whether by sitting an examination or by no-show, no refund will be made.</li>
  <li>Payment service fees charged by the payment platform are non-refundable.</li>
  <li>No refund is available where your scores or results are cancelled, or your participation is terminated, for violation of the ${DSA_INTEGRITY_POLICY_LINK} or examination rules.</li>
</ul>

<p><strong>5. Assessment integrity</strong></p>
<p>DSA may review, withhold, cancel, or investigate registrations, assessment sessions, or score reports when integrity concerns, policy violations, or unverifiable information arise. The full ${DSA_INTEGRITY_POLICY_LINK} is published on the DSA website.</p>

<p><strong>6. Updates</strong></p>
<p>DSA schedules, policies, and administrative procedures may be updated by cycle. The version published at the time of purchase applies to your Pass, unless otherwise required by law.</p>

<p>For questions, contact <a href="mailto:contact@eae.international">contact@eae.international</a>.</p>
`.trim()

/** Restore DSA Integrity Policy links when admin text is pasted from Word/txt without HTML. */
export function linkifyDsaIntegrityPolicy(html) {
  if (!html || !/DSA Integrity Policy/i.test(html)) return html || ''
  let result = ''
  let cursor = 0
  const re = /DSA Integrity Policy/gi
  let match
  while ((match = re.exec(html)) !== null) {
    const start = match.index
    const before = html.slice(0, start)
    const openA = before.lastIndexOf('<a ')
    const closeA = before.lastIndexOf('</a>')
    result += html.slice(cursor, start)
    if (openA > closeA) {
      result += match[0]
    } else {
      result += DSA_INTEGRITY_POLICY_LINK
    }
    cursor = start + match[0].length
  }
  return result + html.slice(cursor)
}

export function formatPolicyHtml(text) {
  if (!text) return ''
  const trimmed = String(text).trim()
  const html = /<[a-z][\s\S]*>/i.test(trimmed)
    ? trimmed
    : trimmed
      .replace(/\r\n\r\n/g, '<br><br>')
      .replace(/\r\n/g, '<br>')
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>')
  return linkifyDsaIntegrityPolicy(html)
}

/** Full pass terms (single document, including refund policy). */
export function resolvePassTermsHtml() {
  return DSA_PASS_TERMS_HTML
}
